import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, AuthProvider } from '../context/AuthContext';
import { TenantProvider } from '../context/TenantContext';
import { StabilityProvider } from '../context/StabilityContext';
import { ThemeProvider } from '../context/ThemeContext';
import { RouteGuard } from '../core/routeGuard';
import { UserRole } from '../types';

// Layouts
import { AppShell } from '../layout/AppShell';
import { SuperAdminLayout } from '../layout/SuperAdminLayout';
import { EnterpriseLayout } from '../layout/EnterpriseLayout';
import { UserLayout } from '../layout/UserLayout';
import { EmployeeLayout } from '../layout/EmployeeLayout';

// Modules
import { LoginPage, LoginSelectionPage, SignupPage, SelectCompanyPage, UnauthorizedPage, ActivateEnterprisePage, ProfilePage, IndividualSignupPage, EnterpriseSignupPage, VerifyAccountPage } from '../modules/auth';
import { HomePage, PricingPage } from '../modules/landing';
import { PlatformAnalytics, FinancialForensicsUI, CompanyManagement } from '../modules/finance';
import { HealthScoreHeatmap } from '../modules/predictive';
import { DynamicPricingPanel, CheckoutPage } from '../modules/pricing';
import { AdaptiveGovernancePanel } from '../modules/adaptive';
import { DisasterRecoveryPanel } from '../modules/dr';
import { MarginProtectionDashboard } from '../modules/margin';
import { EnterpriseDashboard, UserPanel, UserManagement } from '../modules/strategy';
import { GlobalDashboard } from '../modules/dashboard';
import { PaymentsPanel } from '../modules/pricing/components/PaymentsPanel';
import { AIOrchestrationDashboard } from '../modules/ai-orchestration';
import { RiskHeatmap } from '../modules/risk';
import { ContractManagement } from '../modules/contracts';

export const AppRoutes: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutesContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

const AppRoutesContent: React.FC = () => {
  const { loading } = useAuth();
  if (loading) return null;

  return (
    <TenantProvider>
      <StabilityProvider>
        <Routes>
          <Route element={<AppShell />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<LoginSelectionPage />} />
            <Route path="/login/individual" element={<LoginPage />} />
            <Route path="/login/enterprise" element={<LoginPage />} />
            <Route path="/login/employee" element={<LoginPage />} />
            <Route path="/super-admin/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/individual" element={<IndividualSignupPage />} />
            <Route path="/signup/enterprise" element={<EnterpriseSignupPage />} />
            <Route path="/verify-account" element={<VerifyAccountPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/select-company" element={<SelectCompanyPage />} />
            <Route path="/activate-enterprise" element={<ActivateEnterprisePage />} />
            
            {/* Super Admin Routes */}
            <Route path="/super" element={
              <RouteGuard allowedRoles={[UserRole.SUPER_ADMIN]}>
                <SuperAdminLayout />
              </RouteGuard>
            }>
              <Route path="dashboard" element={<PlatformAnalytics />} />
              <Route path="companies" element={<CompanyManagement />} />
              <Route path="admins" element={<div style={{ padding: '20px' }}>Admins & Companies Management</div>} />
              <Route path="payments" element={<PaymentsPanel />} />
              <Route path="margin" element={<MarginProtectionDashboard />} />
              <Route path="risk" element={<HealthScoreHeatmap />} />
              <Route path="dr" element={<DisasterRecoveryPanel />} />
              <Route path="adaptive" element={<AdaptiveGovernancePanel />} />
              <Route path="pricing" element={<DynamicPricingPanel />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="finance" element={<FinancialForensicsUI />} />
              <Route path="ai-orchestration" element={<AIOrchestrationDashboard />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Enterprise Admin Routes */}
            <Route path="/enterprise" element={
              <RouteGuard allowedRoles={[UserRole.ENTERPRISE_ADMIN]} requireSubscription requireCompanyContext>
                <EnterpriseLayout />
              </RouteGuard>
            }>
              <Route path="dashboard" element={<EnterpriseDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="consumption" element={<div style={{ padding: '20px' }}>Token Consumption Analysis</div>} />
              <Route path="budget" element={<div style={{ padding: '20px' }}>Budget Usage Tracking</div>} />
              <Route path="payments" element={<PaymentsPanel />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="margin" element={<MarginProtectionDashboard />} />
              <Route path="contracts" element={<ContractManagement />} />
              <Route path="ai-orchestration" element={<AIOrchestrationDashboard />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* User Routes */}
            <Route path="/user" element={
              <RouteGuard allowedRoles={[UserRole.USER]}>
                <UserLayout />
              </RouteGuard>
            }>
              <Route path="balance" element={<UserPanel />} />
              <Route path="usage" element={<div style={{ padding: '20px' }}>Usage History</div>} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="payment-history" element={<PaymentsPanel />} />
              <Route path="support" element={<div style={{ padding: '20px' }}>Support</div>} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Employee Routes */}
            <Route path="/employee" element={
              <RouteGuard allowedRoles={[UserRole.EMPLOYEE]}>
                <EmployeeLayout />
              </RouteGuard>
            }>
              <Route path="dashboard" element={<div style={{ padding: '20px' }}>Employee Dashboard</div>} />
              <Route path="support" element={<div style={{ padding: '20px' }}>Support</div>} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Routes>
      </StabilityProvider>
    </TenantProvider>
  );
};

const HomeRedirect = () => {
  const { loading } = useAuth();
  
  if (loading) return null;
  
  return <Navigate to="/home" replace />;
};
